import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { useToastManager } from "@porsche-design-system/components-react"
import type { InteriorOptionView } from "@slfinrtl/ui-bff-routes"
import { getInteriorOptionsRoute, setSelectedInteriorRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useMemo, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { useBffDynamic } from "src/hooks"
import type { SelectCatalogInteriorContainerProps } from "./SelectCatalogInteriorContainer"
import type { SelectCatalogInteriorColorProps } from "./view/SelectCatalogInteriorColor"

export const useSelectCatalogInterior: (props: SelectCatalogInteriorContainerProps) => {
	viewProps: SelectCatalogInteriorColorProps | undefined
} = ({ currentInterior, inventoryId, onInteriorSelected, orderType, showCustomInteriorContainer }) => {
	const { formatMessage } = useIntl()
	const { locale } = useDealerContext()

	const { addMessage } = useToastManager()
	const addMessageRef = useRef(addMessage)

	const [selectedOptionCode, setSelectedOptionCode] = useState<string | undefined>(
		currentInterior?.optionCode ?? undefined,
	)
	const [allOptions, setAllOptions] = useState<InteriorOptionView[]>([])
	const [selectedColorTagKey, setSelectedColorTagKey] = useState<string | null>(null)
	const [selectedMaterialKey, setSelectedMaterialKey] = useState<string | null>(null)
	const [searchText, setSearchText] = useState<string>("")
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

	const getInteriorOptions = useBffDynamic(getInteriorOptionsRoute, "inline")
	useEffect(() => {
		if (orderType.code) {
			getInteriorOptions({
				body: undefined,
				pathParams: {},
				queryParams: {
					"model-year": orderType.modelYear.toString(),
					"order-type-code": orderType.code,
				},
			})
				.then(setAllOptions)
				.finally(() => setIsLoading(false))
		} else {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setAllOptions([])
			setIsLoading(false)
		}
	}, [getInteriorOptions, orderType])

	const filteredOptions = useMemo(
		() => calculateFilteredOptions(allOptions, selectedColorTagKey, selectedMaterialKey, searchText),
		[allOptions, selectedColorTagKey, selectedMaterialKey, searchText],
	)

	const filteredTags = useMemo(() => calculateColorTagOptions(allOptions), [allOptions])

	const filteredMaterials = useMemo(() => calculateMaterialOptions(allOptions), [allOptions])

	const submitSelectedInterior = useBffDynamic(setSelectedInteriorRoute, "inline")
	const onSubmit = async () => {
		if (selectedOptionCode) {
			setIsSubmitting(true)

			try {
				await submitSelectedInterior({
					body: {
						optionCode: selectedOptionCode,
					},
					pathParams: {},
					queryParams: { "inventory-id": inventoryId },
				})
			} catch {
				addMessageRef.current({
					state: "info",
					text: formatMessage(formMessages.errorGeneral),
				})
				return
			} finally {
				setIsSubmitting(false)
			}

			const selectedOption = allOptions.find((option) => option.optionCode === selectedOptionCode)
			if (selectedOption) {
				onInteriorSelected({
					color: {
						cockpitColorHexCode: selectedOption.color.cockpitColorHexCode,
						cockpitStitchingColorHexCode: selectedOption.color.cockpitStitchingColorHexCode,
						seatColorHexCode: selectedOption.color.seatColorHexCode,
						seatStitchingColorHexCode: selectedOption.color.seatStitchingColorHexCode,
						tagKey: undefined,
					},
					materialKey: undefined,
					name: {
						[locale]: selectedOption.name,
					},
					optionCode: selectedOption.optionCode,
				})
			}
		}
	}

	return {
		viewProps: !isLoading
			? {
					interior: {
						options: filteredOptions,
						setValue: setSelectedOptionCode,
						value: selectedOptionCode,
					},
					isSubmitting,
					material: {
						options: filteredMaterials,
						setValue: setSelectedMaterialKey,
						value: selectedMaterialKey,
					},
					onSubmit,
					searchText: {
						setValue: setSearchText,
						value: searchText,
					},
					showCustomInteriorContainer,
					tags: {
						options: filteredTags,
						setValue: setSelectedColorTagKey,
						value: selectedColorTagKey,
					},
				}
			: undefined,
	}
}

const calculateFilteredOptions = (
	allOptions: InteriorOptionView[],
	selectedColorTagKey: string | null,
	selectedMaterialKey: string | null,
	searchText: string,
): InteriorOptionView[] => {
	let filteredOptions = allOptions
	if (selectedColorTagKey) {
		filteredOptions = filteredOptions.filter(
			(interiorOption) => interiorOption.color.colorTag.key === selectedColorTagKey,
		)
	}
	if (selectedMaterialKey) {
		filteredOptions = filteredOptions.filter(
			(interiorOption) => interiorOption.material?.key === selectedMaterialKey,
		)
	}
	if (searchText !== undefined && searchText.length > 0) {
		const lowerSearchText = searchText.toLowerCase()
		filteredOptions = filteredOptions.filter(
			(interiorOption) =>
				interiorOption.name?.toLocaleLowerCase()?.includes(lowerSearchText) ||
				interiorOption.optionCode.toLocaleLowerCase()?.includes(lowerSearchText) ||
				interiorOption?.material?.name?.toLocaleLowerCase()?.includes(lowerSearchText),
		)
	}
	return filteredOptions
}

const calculateColorTagOptions = (
	interiorOptions: InteriorOptionView[],
): SelectCatalogInteriorColorProps["tags"]["options"] => {
	const uniqueColorTags = interiorOptions?.reduce<Record<string, InteriorOptionView["color"]["colorTag"]>>(
		(result, current) => {
			if (!(current.color.colorTag.key in result)) {
				result[current.color.colorTag.key] = current.color.colorTag
			}

			return result
		},
		{},
	)

	return Object.values(uniqueColorTags).map((colorTag) => ({
		label: colorTag.name,
		value: colorTag.key,
	}))
}

interface Material {
	key: string
	name: string
}

const calculateMaterialOptions = (
	interiorOptions: InteriorOptionView[],
): SelectCatalogInteriorColorProps["tags"]["options"] => {
	const uniqueMaterials = interiorOptions?.reduce<Record<string, Material>>((result, current) => {
		if (current.material && !(current.material.key in result)) {
			result[current.material.key] = current.material
		}

		return result
	}, {})

	return Object.values(uniqueMaterials).map((material) => ({
		label: material.name,
		value: material.key,
	}))
}
